// Properties hook - matching admin-web pattern with RN enhancements
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLazyListPropertiesQuery } from '@/store/api/admin/propertiesApi';
import { IProperty, IPropertyRowData } from '@/types/admin/properties';
import { useAdminUser } from './useAdminUser';
import { formatPropertyCsvData } from '@/utils/admin/properties';
import { useDataTableState } from '@/contexts/admin/DataTableContext';
import { IFilterModel, ISortModel } from '@/types/admin/dataTable';
import { applyTextFilter, compareValues } from '@/utils/admin/filterUtils';

export interface UsePropertiesReturn {
  // Data
  data: {
    rows: IProperty[];
    count: number;
  };
  tableRows: IPropertyRowData[];
  filteredTableRows: IPropertyRowData[];
  csvData: (string | number)[][];
  
  // Loading and error states
  isLoading: boolean;
  error: any;
  
  // Property selection
  selectedProperty: IProperty | undefined;
  setSelectedProperty: (property: IProperty | undefined) => void;
  
  // Panel states
  showCreatePanel: boolean;
  setShowCreatePanel: (show: boolean) => void;
  showEditPanel: boolean;
  setShowEditPanel: (show: boolean) => void;
  
  // Filter and Sort
  searchQuery: string;
  filterModel: IFilterModel;
  sortModel: ISortModel[];
  
  // Actions
  refresh: () => void;
  formatCsvData: (newData: any) => void;
}

export const useProperties = (): UsePropertiesReturn => {
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  const table = useDataTableState('admin:properties');
  
  // API query
  const [getPropertyList, { data, isLoading, isFetching, isUninitialized, error }] = 
    useLazyListPropertiesQuery();
  
  // Local state
  const [csvData, setCsvData] = useState<(string | number)[][]>([
    ['marketingName', 'externalId', 'address']
  ]);
  const [selectedProperty, setSelectedProperty] = useState<IProperty | undefined>();
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);

  // Track if CSV has been initialized to prevent circular dependency
  const hasFormattedCsv = useRef(false);

  // Get filter/sort from context
  const searchQuery = table.searchQuery;
  const filterModel = table.filterModel;
  const sortModel = table.sortModel as ISortModel[];
  
  /**
   * Fetch properties list
   */
  const fetchProperties = useCallback(() => {
    if (selectedPropertyId) {
      getPropertyList(`?propertyId=${selectedPropertyId}`);
    } else {
      getPropertyList();
    }
  }, [selectedPropertyId, getPropertyList]);
  
  /**
   * Initial fetch and refresh on property change
   */
  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);
  
  /**
   * Format CSV data for export
   */
  const formatCsvDataCallback = useCallback((newData: any) => {
    const formatted = formatPropertyCsvData(newData);
    setCsvData(formatted);
    hasFormattedCsv.current = true;
  }, []);
  
  /**
   * Auto-format CSV data when data changes (only once on initial load)
   */
  useEffect(() => {
    if (data && !hasFormattedCsv.current) {
      formatCsvDataCallback(data);
    }
  }, [data, formatCsvDataCallback]);
  
  /**
   * Convert API data to table rows
   */
  const tableRows = useMemo<IPropertyRowData[]>(() => {
    if (!data?.response?.rows) return [];
    
    return data.response.rows.map((property) => ({
      id: property.id,
      marketingName: property.marketingName,
      externalId: property.externalId,
      address: property.address || '',
    }));
  }, [data]);

  /**
   * Apply client-side filtering and sorting (matching admin-web behavior)
   */
  const filteredTableRows = useMemo<IPropertyRowData[]>(() => {
    let filtered = [...tableRows];

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(row =>
        row.marketingName.toLowerCase().includes(query) ||
        row.externalId.toString().toLowerCase().includes(query) ||
        row.address.toLowerCase().includes(query)
      );
    }

    // Apply filters using shared utilities
    if (filterModel.items && filterModel.items.length > 0) {
      filterModel.items.forEach(filter => {
        if (!filter.value) return;
        
        switch (filter.field) {
          case 'marketingName':
            filtered = filtered.filter(row => 
              applyTextFilter(row.marketingName, filter.value, filter.operator)
            );
            break;
            
          case 'externalId':
            filtered = filtered.filter(row => 
              applyTextFilter(row.externalId, filter.value, filter.operator)
            );
            break;
            
          case 'address':
            filtered = filtered.filter(row => 
              applyTextFilter(row.address, filter.value, filter.operator)
            );
            break;
        }
      });
    }

    // Apply sorting using shared utility
    if (sortModel.length > 0) {
      const sort = sortModel[0];
      const field = sort.field as keyof IPropertyRowData;

      filtered.sort((a, b) => {
        const aVal = a[field];
        const bVal = b[field];
        return compareValues(aVal, bVal, sort.sort);
      });
    }

    return filtered;
  }, [tableRows, searchQuery, filterModel, sortModel]);
  
  /**
   * Safe data access with defaults
   */
  const safeData = useMemo(() => ({
    rows: data?.response?.rows || [],
    count: data?.response?.count || 0,
  }), [data]);
  
  return {
    // Data
    data: safeData,
    tableRows,
    filteredTableRows,
    csvData,
    
    // Loading and error states
    isLoading: isLoading || isFetching || isUninitialized,
    error,
    
    // Property selection
    selectedProperty,
    setSelectedProperty,
    
    // Panel states
    showCreatePanel,
    setShowCreatePanel,
    showEditPanel,
    setShowEditPanel,
    
    // Filter and Sort
    searchQuery,
    filterModel,
    sortModel,
    
    // Actions
    refresh: fetchProperties,
    formatCsvData: formatCsvDataCallback,
  };
};

